import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { LineItemInvoiceStatus, OrderInvoiceStatus } from "@acto/gql/graphql";
import { Card, MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { LineItemList, LineItemRow } from "@acto/taskpane/field-sales-2/components/primitives/LineItemRow/LineItemRow";
import type { LineItemDisplay, OrderDetails } from "@acto/taskpane/field-sales-2/data-access/useOrderDetails";
import { useOrdersInRevenueDetailEnabled } from "@acto/taskpane/field-sales-2/data-access/useOrdersInRevenueDetailEnabled";

export const OrderHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const StatusFigures = styled("dl")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.fieldSalesTwo.spacing[16],
  margin: 0,
}));

const StatusFigure = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  "& dd": {
    margin: 0,
    fontFamily: theme.fieldSalesTwo.font.family,
    fontSize: theme.fieldSalesTwo.font.size[15],
    lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
    fontWeight: theme.fieldSalesTwo.font.weight[500],
    color: theme.fieldSalesTwo.colors.neutral[900],
  },
}));

const LineItemsCardRoot = styled(Card)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[32],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
}));

export interface LineItemGroup {
  key: "open" | "invoiced" | "all";
  title: string;
  items: LineItemDisplay[];
}

function useInvoicing(order: OrderDetails) {
  const isOrdersViewEnabled = useOrdersInRevenueDetailEnabled();
  const invoiceStatus = isOrdersViewEnabled ? order.invoiceStatus : null;
  const formattedInvoiced = order.formattedInvoicedIncomingOrder;

  return invoiceStatus != null && formattedInvoiced != null ? { invoiceStatus, formattedInvoiced } : null;
}

export function OrderStatusFigures({ order }: { order: OrderDetails }) {
  const t = useTranslations("fieldSalesTwo");
  const invoicing = useInvoicing(order);
  if (!invoicing) return null;

  return (
    <StatusFigures>
      <StatusFigure>
        <MetaLabel as="dt">{t("orderDetails.status")}</MetaLabel>
        <dd>{t(`analysis.orders.invoiceStatus.${invoicing.invoiceStatus}`)}</dd>
      </StatusFigure>
      <StatusFigure>
        <MetaLabel as="dt">{t("orderDetails.invoiced")}</MetaLabel>
        <dd>
          {invoicing.invoiceStatus === OrderInvoiceStatus.Invoiced
            ? t("orderDetails.invoicedInFull")
            : t("orderDetails.invoicedOfTotal", {
                invoiced: invoicing.formattedInvoiced,
                total: order.formattedIncomingOrder,
              })}
        </dd>
      </StatusFigure>
    </StatusFigures>
  );
}

/**
 * Open and invoiced line items as separate groups, empty ones dropped. One group of every item while
 * the order's invoicing is unknown or the orders view is off.
 */
export function useLineItemGroups(order: OrderDetails): LineItemGroup[] {
  const t = useTranslations("fieldSalesTwo");
  const invoicing = useInvoicing(order);

  if (!invoicing) return [{ key: "all", title: t("orderDetails.lineItems.title"), items: order.lineItems }];

  const groups: LineItemGroup[] = [
    {
      key: "open",
      title: t("orderDetails.lineItems.open"),
      items: order.lineItems.filter((item) => item.invoiceStatus === LineItemInvoiceStatus.Open),
    },
    {
      key: "invoiced",
      title: t("orderDetails.lineItems.invoiced"),
      items: order.lineItems.filter((item) => item.invoiceStatus === LineItemInvoiceStatus.Invoiced),
    },
  ];
  return groups.filter((group) => group.items.length > 0);
}

export function LineItems({ items }: { items: LineItemDisplay[] }) {
  return (
    <LineItemList role="list">
      {items.map((item) => (
        <LineItemRow
          key={item.id}
          name={item.productName}
          price={item.formattedTotal}
          meta={item.productExternalReference}
        />
      ))}
    </LineItemList>
  );
}

export function LineItemsCard({ items }: { items: LineItemDisplay[] }) {
  return (
    <LineItemsCardRoot>
      <LineItems items={items} />
    </LineItemsCardRoot>
  );
}
